import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionTitle from "@/components/feature/SectionTitle";

const values = [
  {
    icon: "ri-film-line",
    title: "内容专业",
    text: "由资深编辑团队每日筛选与整理电影资讯，坚持真实、客观、有价值的内容标准。",
  },
  {
    icon: "ri-compass-3-line",
    title: "智能推荐",
    text: "通过类型、地区、年份等多维标签体系，帮你快速找到与自己口味契合的影片。",
  },
  {
    icon: "ri-heart-3-line",
    title: "面向影迷",
    text: "从热门新作到经典重温，我们关注每一位影迷真实的观影需求与情感共鸣。",
  },
  {
    icon: "ri-shield-check-line",
    title: "信息清晰",
    text: "所有影片信息均标注来源与更新时间，力求让每一条内容都可追溯、可信赖。",
  },
];

const stats = [
  { value: "18+", label: "收录影片" },
  { value: "12+", label: "资讯内容" },
  { value: "4", label: "精选专题" },
  { value: "8", label: "影片分类" },
];

export default function About() {
  useDocumentMeta({
    title: "关于我们 - 秋霞电影影视门户介绍",
    description:
      "了解秋霞电影：一个专注于电影资讯与影片推荐的专业影视门户，为电影爱好者提供权威、丰富、清晰的影视内容服务。",
    keywords: "秋霞电影,关于我们,影视门户,电影资讯平台",
  });

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-9">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        </div>
      </div>

      {/* intro */}
      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-movie-2-line"></i></span>
              关于秋霞电影
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
              让每一次观影，都值得被认真对待
            </h1>
            <p className="mt-5 text-[15px] leading-8 text-foreground-700">
              秋霞电影是一家专注于电影资讯和影片推荐的专业影视门户，面向广大电影爱好者、影评人及行业关注者。
              网站内容涵盖最新电影新闻、影片推荐、影片详情展示及影视行业资讯等，注重信息权威性与内容丰富性。
            </p>
            <p className="mt-4 text-[15px] leading-8 text-foreground-700">
              我们希望帮助每一位用户轻松发现和了解各类优秀电影 —— 无论是正在热映的新作，
              还是值得反复回味的经典，都能在秋霞电影找到属于它的位置。
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/category/popular" className="rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap">
                浏览热门影片
              </Link>
              <Link to="/news" className="rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 whitespace-nowrap">
                查看影视资讯
              </Link>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <div className="aspect-[4/3] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20film%20portal%20workspace%20with%20a%20large%20monitor%20showing%20movie%20posters%2C%20warm%20orange%20accent%20lighting%20against%20deep%20navy%20walls%2C%20clean%20editorial%20interior%20photography%20style&width=1000&height=750&seq=qx-ab-01&orientation=landscape"
                alt="秋霞电影工作场景"
                title="秋霞电影 - 影视门户"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* stats */}
      <section className="bg-background-100">
        <div className="mx-auto grid max-w-[1280px] grid-cols-2 gap-4 px-4 py-10 md:grid-cols-4 md:px-6 md:py-12">
          {stats.map((s) => (
            <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 p-5 text-center">
              <p className="font-heading text-2xl font-bold text-primary-700 md:text-3xl">{s.value}</p>
              <p className="mt-1.5 text-sm text-foreground-600">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* values */}
      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <SectionTitle
          eyebrow="我们的坚持"
          icon={<i className="ri-heart-2-line"></i>}
          title="为什么选择秋霞电影"
          subtitle="用专业与真诚，陪你发现每一部好电影"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v) => (
            <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 text-xl">
                <i className={v.icon}></i>
              </span>
              <h3 className="mt-4 text-base font-semibold text-foreground-950">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* contact */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <SectionTitle
            eyebrow="联系我们"
            icon={<i className="ri-customer-service-2-line"></i>}
            title="欢迎与我们取得联系"
            subtitle="无论是内容建议、合作洽谈还是反馈意见，都欢迎随时来信"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <a href="mailto:contact@qiuxiamovie.com" className="group rounded-lg border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 text-xl">
                <i className="ri-mail-send-line"></i>
              </span>
              <h3 className="mt-4 text-base font-semibold text-foreground-950">用户反馈邮箱</h3>
              <p className="mt-2 text-sm text-foreground-600">对网站内容有建议或发现问题，欢迎写信告诉我们。</p>
              <p className="mt-3 text-sm font-medium text-primary-700 group-hover:text-accent-700 transition-colors">contact@qiuxiamovie.com</p>
            </a>
            <a href="mailto:press@qiuxiamovie.com" className="group rounded-lg border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700 text-xl">
                <i className="ri-briefcase-line"></i>
              </span>
              <h3 className="mt-4 text-base font-semibold text-foreground-950">商务合作</h3>
              <p className="mt-2 text-sm text-foreground-600">影片宣传、内容合作与媒体联络请使用以下邮箱。</p>
              <p className="mt-3 text-sm font-medium text-primary-700 group-hover:text-accent-700 transition-colors">press@qiuxiamovie.com</p>
            </a>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-800 text-xl">
                <i className="ri-map-pin-user-line"></i>
              </span>
              <h3 className="mt-4 text-base font-semibold text-foreground-950">内容服务范围</h3>
              <p className="mt-2 text-sm text-foreground-600">线上内容服务，覆盖全国地区的电影爱好者。</p>
              <p className="mt-3 text-sm font-medium text-foreground-800">服务时间：工作日 9:00 - 18:00</p>
            </div>
          </div>
        </div>
      </section>

      {/* copyright */}
      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading text-lg font-bold text-foreground-950">版权信息与声明</h2>
          <div className="mt-4 space-y-3 text-sm leading-7 text-foreground-600">
            <p>
              本站所展示的影片信息、资讯内容均为演示示例，仅用于产品功能展示，不代表真实上映或发行情况。
            </p>
            <p>
              站内使用的图片为示意性质的美术素材，版权归原作者所有。如涉及版权问题，请通过上方反馈邮箱与我们联系，我们将及时处理。
            </p>
            <p>© 2024 秋霞电影 QiuxiaMovie. 保留所有权利。</p>
          </div>
        </div>
      </section>
    </div>
  );
}